import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import "../App.css";
import { getTokenInternal } from "./useToken";
import Nav from "./Navigation.js";

const API = process.env.REACT_APP_API_HOST;

async function uploadImage(file, token) {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API}/api/images/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token.access_token}` },
    body: form,
  });
  if (!response.ok) throw new Error("Image upload failed");
  return response.json();
}

// Lets a floor image be set three ways: upload a new file, paste a known
// R2 URL directly, or browse/select from images already uploaded (e.g. by
// a different environment sharing the same R2 bucket, like staging).
function FloorImageSource({ token, file, setFile, urlValue, setUrlValue, currentLabel }) {
  const [showBrowser, setShowBrowser] = useState(false);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  const toggleBrowser = async () => {
    const opening = !showBrowser;
    setShowBrowser(opening);
    if (opening && images.length === 0) {
      setLoadingImages(true);
      try {
        const response = await fetch(`${API}/api/images`, {
          headers: { Authorization: `Bearer ${token.access_token}` },
        });
        if (response.ok) setImages(await response.json());
      } finally {
        setLoadingImages(false);
      }
    }
  };

  return (
    <div className="space-y-2 border border-gray-700 rounded p-2">
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          {currentLabel || "Upload an image"}
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            setFile(e.target.files[0]);
            setUrlValue("");
          }}
        />
      </div>
      <div className="text-xs text-gray-500">— or —</div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Paste a known image URL</label>
        <input
          type="text"
          className="w-full p-2 text-black rounded"
          value={urlValue}
          onChange={(e) => {
            setUrlValue(e.target.value);
            if (e.target.value) setFile(null);
          }}
          placeholder="https://..."
        />
      </div>
      <div>
        <button
          type="button"
          onClick={toggleBrowser}
          className="px-2 py-1 rounded bg-gray-700 text-xs hover:bg-gray-600"
        >
          {showBrowser ? "Hide existing images" : "Browse existing images"}
        </button>
        {showBrowser && (
          <div className="mt-2 max-h-48 overflow-y-auto border border-gray-700 rounded p-2 grid grid-cols-4 gap-2">
            {loadingImages && <div className="text-xs text-gray-400 col-span-4">Loading...</div>}
            {!loadingImages && images.length === 0 && (
              <div className="text-xs text-gray-400 col-span-4">No images found.</div>
            )}
            {images.map((img) => (
              <button
                type="button"
                key={img.filename}
                onClick={() => {
                  setUrlValue(img.url);
                  setFile(null);
                  setShowBrowser(false);
                }}
                className="flex flex-col items-center gap-1 hover:opacity-80"
                title={img.filename}
              >
                <img
                  src={img.url}
                  alt={img.filename}
                  className="w-16 h-16 object-cover rounded border border-gray-600"
                />
                <span className="text-[10px] text-gray-400 truncate w-16">{img.filename}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemSwatch({ item, sizeClass }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (item.image_url && !imgFailed) {
    return (
      <img
        src={item.image_url}
        alt={item.name}
        className={`${sizeClass} rounded object-cover flex-shrink-0`}
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <span
      className={`${sizeClass} rounded-full inline-block flex-shrink-0`}
      style={{ backgroundColor: item.color }}
    />
  );
}

function PhotoPreview({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <p className="text-sm text-red-400">Photo failed to load.</p>;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className || "max-w-full rounded"}
      onError={() => setFailed(true)}
    />
  );
}

function MarkerDot({ marker, onClick, highlightedItemId, editMode, containerRef, onMove }) {
  const [dragPos, setDragPos] = useState(null);
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const color = marker.items[0] ? marker.items[0].color : "#999999";
  const isMatch =
    highlightedItemId != null &&
    marker.items.some((i) => i.legend_item_id === highlightedItemId);
  const isDimmed = highlightedItemId != null && !isMatch;

  const clampPercent = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    draggingRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };

    const handlePointerMove = (moveEvent) => {
      const dx = moveEvent.clientX - startRef.current.x;
      const dy = moveEvent.clientY - startRef.current.y;
      if (!draggingRef.current && Math.hypot(dx, dy) > 4) {
        draggingRef.current = true;
      }
      if (draggingRef.current && containerRef.current) {
        setDragPos(clampPercent(moveEvent.clientX, moveEvent.clientY));
      }
    };

    const handlePointerUp = (upEvent) => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      if (draggingRef.current && containerRef.current) {
        const pos = clampPercent(upEvent.clientX, upEvent.clientY);
        setDragPos(null);
        onMove(marker, pos.x, pos.y);
      } else {
        onClick();
      }
      draggingRef.current = false;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const pos = dragPos || { x: marker.x_percent, y: marker.y_percent };

  return (
    <div
      onPointerDown={editMode ? handlePointerDown : undefined}
      onClick={editMode ? (e) => e.stopPropagation() : onClick}
      title={marker.items.map((i) => i.code).join(", ") || "Empty marker"}
      className={`absolute rounded-full border-2 border-white shadow-lg transition-all ${
        editMode ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } ${
        isMatch
          ? "w-7 h-7 ring-4 ring-yellow-300 animate-pulse z-10"
          : "w-5 h-5 hover:scale-125"
      } ${isDimmed ? "opacity-20" : ""}`}
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        transform: "translate(-50%, -50%)",
        backgroundColor: color,
      }}
    >
      {marker.items.length > 1 && (
        <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
          {marker.items.length}
        </span>
      )}
    </div>
  );
}

function MarkerViewModal({ marker, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 text-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Marker Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        {marker.items.length === 0 && <p className="text-gray-400">No items tagged here.</p>}
        <ul className="space-y-2 mb-4">
          {marker.items.map((item) => (
            <li key={item.id} className="flex items-center gap-2">
              <ItemSwatch item={item} sizeClass="w-8 h-8" />
              <span className="font-semibold">{item.code}</span>
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
        {marker.notes && <p className="mb-4 text-gray-300">{marker.notes}</p>}
        {marker.photo_url && <PhotoPreview src={marker.photo_url} alt="Marker spot" />}
      </div>
    </div>
  );
}

function MarkerEditModal({ marker, legend, token, onClose, onSaved, onDeleted }) {
  const isNew = !marker.id;
  const [notes, setNotes] = useState(marker.notes || "");
  const [photoUrl, setPhotoUrl] = useState(marker.photo_url || "");
  const [uploading, setUploading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(
    () => new Set(marker.items.map((i) => i.legend_item_id))
  );

  const toggleItem = (legendItemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(legendItemId)) {
        next.delete(legendItemId);
      } else {
        next.add(legendItemId);
      }
      return next;
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadImage(file, token);
      setPhotoUrl(result.url);
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const items = Array.from(selectedItemIds).map((legend_item_id) => ({
      legend_item_id,
    }));
    const payload = {
      floor_id: marker.floor_id,
      x_percent: marker.x_percent,
      y_percent: marker.y_percent,
      notes,
      photo_url: photoUrl || null,
      items,
    };
    const url = isNew
      ? `${API}/api/loot/markers/create`
      : `${API}/api/loot/markers/${marker.id}`;
    const response = await fetch(url, {
      method: isNew ? "POST" : "PUT",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      onSaved();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this marker?")) return;
    await fetch(`${API}/api/loot/markers/${marker.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    onDeleted();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 text-white rounded-lg p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isNew ? "Add Marker" : "Edit Marker"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>

        <div className="mb-4">
          <p className="font-semibold mb-2">Items at this spot:</p>
          <div className="space-y-2">
            {legend.map((item) => (
              <div key={item.id} className="border border-gray-700 rounded p-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedItemIds.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                  />
                  <ItemSwatch item={item} sizeClass="w-6 h-6" />
                  <span className="font-semibold">{item.code}</span>
                  <span>{item.name}</span>
                </label>
              </div>
            ))}
            {legend.length === 0 && <p className="text-gray-400">No legend items yet — add some first.</p>}
          </div>
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Notes</label>
          <textarea
            className="w-full p-2 text-black rounded"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. top drawer of the desk"
          />
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Photo (optional)</label>
          <input type="file" accept="image/*" onChange={handlePhotoChange} />
          {uploading && <p className="text-sm text-gray-400">Uploading...</p>}
          {photoUrl && <PhotoPreview src={photoUrl} alt="Spot" className="max-w-xs mt-2 rounded" />}
        </div>

        <div className="flex justify-between">
          {!isNew && (
            <button onClick={handleDelete} className="bg-red-700 hover:bg-red-800 px-4 py-2 rounded">
              Delete
            </button>
          )}
          <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded ml-auto">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function LegendItemEditRow({ item, gameId, token, onDone, onChanged }) {
  const [code, setCode] = useState(item.code);
  const [name, setName] = useState(item.name);
  const [color, setColor] = useState(item.color);
  const [privateTesting, setPrivateTesting] = useState(item.private_testing || false);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!code || !name) return;
    setSaving(true);
    try {
      let image_url = item.image_url || null;
      if (imageFile) {
        const uploaded = await uploadImage(imageFile, token);
        image_url = uploaded.url;
      }
      const response = await fetch(`${API}/api/loot/legend/${item.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          game_id: gameId,
          code,
          name,
          color,
          image_url,
          sort_order: item.sort_order,
          private_testing: privateTesting,
        }),
      });
      if (response.ok) {
        onChanged();
        onDone();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-700 rounded p-2 mt-1 space-y-2">
      <input className="w-full p-2 text-black rounded" value={code} onChange={(e) => setCode(e.target.value)} />
      <input className="w-full p-2 text-black rounded" value={name} onChange={(e) => setName(e.target.value)} />
      <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          Replace image (optional -- leave blank to keep the current one)
        </label>
        <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm">
        <input
          type="checkbox"
          checked={privateTesting}
          onChange={(e) => setPrivateTesting(e.target.checked)}
        />
        Private testing only
      </label>
      <div className="flex gap-2">
        <button onClick={onDone} className="px-3 py-1 rounded bg-gray-700 text-sm">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-sm ml-auto"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function LegendEditorModal({ gameId, legend, token, onClose, onChanged }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState("#ff0000");
  const [privateTesting, setPrivateTesting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleAdd = async () => {
    if (!code || !name) return;
    setUploading(true);
    try {
      let image_url = null;
      if (imageFile) {
        const uploaded = await uploadImage(imageFile, token);
        image_url = uploaded.url;
      }
      const response = await fetch(`${API}/api/loot/legend/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          game_id: gameId,
          code,
          name,
          color,
          image_url,
          sort_order: legend.length,
          private_testing: privateTesting,
        }),
      });
      if (response.ok) {
        setCode("");
        setName("");
        setImageFile(null);
        setPrivateTesting(false);
        onChanged();
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this legend item? Markers using it will lose that tag.")) return;
    await fetch(`${API}/api/loot/legend/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    onChanged();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 text-white rounded-lg p-6 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Legend</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        <ul className="space-y-2 mb-4">
          {legend.map((item) => (
            <li key={item.id}>
              <div className="flex items-center gap-2">
                <ItemSwatch item={item} sizeClass="w-8 h-8" />
                <span className="font-semibold">{item.code}</span>
                <span>{item.name}</span>
                {item.private_testing && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-800 text-purple-200 font-semibold">
                    Private
                  </span>
                )}
                <button
                  onClick={() => setEditingId(editingId === item.id ? null : item.id)}
                  className="ml-auto text-blue-400 hover:text-blue-300 text-sm"
                >
                  Edit
                </button>
                <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300 text-sm">
                  Remove
                </button>
              </div>
              {editingId === item.id && (
                <LegendItemEditRow
                  item={item}
                  gameId={gameId}
                  token={token}
                  onDone={() => setEditingId(null)}
                  onChanged={onChanged}
                />
              )}
            </li>
          ))}
        </ul>
        <div className="border-t border-gray-700 pt-4 space-y-2">
          <input
            className="w-full p-2 text-black rounded"
            placeholder="Code (e.g. HK)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <input
            className="w-full p-2 text-black rounded"
            placeholder="Name (e.g. Helicopter Keys)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
          <div>
            <label className="block text-sm text-gray-400 mb-1">Default image (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={privateTesting}
              onChange={(e) => setPrivateTesting(e.target.checked)}
            />
            Private testing only
          </label>
          <button
            onClick={handleAdd}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded w-full"
          >
            {uploading ? "Uploading..." : "Add Legend Item"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MapEditRow({ map, gameId, token, onDone, onChanged }) {
  const [name, setName] = useState(map.name);
  const [description, setDescription] = useState(map.description || "");
  const [privateTesting, setPrivateTesting] = useState(map.private_testing || false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name) return;
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/loot/maps/${map.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          game_id: gameId,
          name,
          description: description || null,
          sort_order: map.sort_order,
          private_testing: privateTesting,
        }),
      });
      if (response.ok) {
        onChanged();
        onDone();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-700 rounded p-2 mt-1 space-y-2">
      <input className="w-full p-2 text-black rounded" value={name} onChange={(e) => setName(e.target.value)} />
      <input
        className="w-full p-2 text-black rounded"
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <label className="flex items-center gap-2 cursor-pointer text-sm">
        <input
          type="checkbox"
          checked={privateTesting}
          onChange={(e) => setPrivateTesting(e.target.checked)}
        />
        Private testing only (hidden from everyone else)
      </label>
      <div className="flex gap-2">
        <button onClick={onDone} className="px-3 py-1 rounded bg-gray-700 text-sm">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-sm ml-auto"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function MapManagerModal({ gameId, maps, token, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [privateTesting, setPrivateTesting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleAdd = async () => {
    if (!name) return;
    const response = await fetch(`${API}/api/loot/maps/create`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        game_id: gameId,
        name,
        description: description || null,
        sort_order: maps.length,
        private_testing: privateTesting,
      }),
    });
    if (response.ok) {
      setName("");
      setDescription("");
      setPrivateTesting(false);
      onChanged();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this map and all its floors/markers?")) return;
    await fetch(`${API}/api/loot/maps/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    onChanged();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 text-white rounded-lg p-6 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Maps</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        <ul className="space-y-2 mb-4">
          {maps.map((map) => (
            <li key={map.id}>
              <div className="flex items-center gap-2">
                <span>{map.name}</span>
                {map.private_testing && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-800 text-purple-200 font-semibold">
                    Private
                  </span>
                )}
                <button
                  onClick={() => setEditingId(editingId === map.id ? null : map.id)}
                  className="ml-auto text-blue-400 hover:text-blue-300 text-sm"
                >
                  Edit
                </button>
                <button onClick={() => handleDelete(map.id)} className="text-red-400 hover:text-red-300 text-sm">
                  Remove
                </button>
              </div>
              {editingId === map.id && (
                <MapEditRow
                  map={map}
                  gameId={gameId}
                  token={token}
                  onDone={() => setEditingId(null)}
                  onChanged={onChanged}
                />
              )}
            </li>
          ))}
        </ul>
        <div className="border-t border-gray-700 pt-4 space-y-2">
          <input
            className="w-full p-2 text-black rounded"
            placeholder="Map name (e.g. Delta-7, The Boat)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="w-full p-2 text-black rounded"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={privateTesting}
              onChange={(e) => setPrivateTesting(e.target.checked)}
            />
            Private testing only (hidden from everyone else)
          </label>
          <button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded w-full">
            Add Map
          </button>
        </div>
      </div>
    </div>
  );
}

function FloorEditRow({ floor, mapId, token, onDone, onChanged }) {
  const [name, setName] = useState(floor.name);
  const [file, setFile] = useState(null);
  const [urlValue, setUrlValue] = useState("");
  const [description, setDescription] = useState(floor.description || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name) return;
    setSaving(true);
    try {
      let image_url = floor.image_url;
      if (file) {
        const uploaded = await uploadImage(file, token);
        image_url = uploaded.url;
      } else if (urlValue.trim()) {
        image_url = urlValue.trim();
      }
      const response = await fetch(`${API}/api/loot/floors/${floor.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          map_id: mapId,
          name,
          image_url,
          sort_order: floor.sort_order,
          description: description || null,
        }),
      });
      if (response.ok) {
        onChanged();
        onDone();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-700 rounded p-2 mt-1 space-y-2">
      <input
        className="w-full p-2 text-black rounded"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <FloorImageSource
        token={token}
        file={file}
        setFile={setFile}
        urlValue={urlValue}
        setUrlValue={setUrlValue}
        currentLabel="Replace image (optional -- leave everything blank to keep the current one)"
      />
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          Heads-up note (optional -- e.g. "This section is randomly generated, positions may not be exact")
        </label>
        <textarea
          className="w-full p-2 text-black rounded"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <button onClick={onDone} className="px-3 py-1 rounded bg-gray-700 text-sm">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-sm ml-auto"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function FloorManagerModal({ mapId, floors, token, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [urlValue, setUrlValue] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingFloorId, setEditingFloorId] = useState(null);

  const handleAdd = async () => {
    if (!name || (!file && !urlValue.trim())) return;
    setUploading(true);
    try {
      let image_url = urlValue.trim();
      if (file) {
        const uploaded = await uploadImage(file, token);
        image_url = uploaded.url;
      }
      const response = await fetch(`${API}/api/loot/floors/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          map_id: mapId,
          name,
          image_url,
          sort_order: floors.length,
          description: description || null,
        }),
      });
      if (response.ok) {
        setName("");
        setFile(null);
        setUrlValue("");
        setDescription("");
        onChanged();
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this floor and all its markers?")) return;
    await fetch(`${API}/api/loot/floors/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    onChanged();
  };

  const handleMove = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= floors.length) return;
    const current = floors[index];
    const target = floors[targetIndex];
    const putFloor = (floor, sort_order) =>
      fetch(`${API}/api/loot/floors/${floor.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          map_id: mapId,
          name: floor.name,
          image_url: floor.image_url,
          sort_order,
        }),
      });
    await Promise.all([
      putFloor(current, target.sort_order),
      putFloor(target, current.sort_order),
    ]);
    onChanged();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 text-white rounded-lg p-6 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Floors</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        <ul className="space-y-2 mb-4">
          {floors.map((floor, index) => (
            <li key={floor.id}>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button
                    onClick={() => handleMove(index, "up")}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => handleMove(index, "down")}
                    disabled={index === floors.length - 1}
                    className="text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>
                <span>{floor.name}</span>
                <button
                  onClick={() => setEditingFloorId(editingFloorId === floor.id ? null : floor.id)}
                  className="ml-auto text-blue-400 hover:text-blue-300 text-sm"
                >
                  Edit
                </button>
                <button onClick={() => handleDelete(floor.id)} className="text-red-400 hover:text-red-300 text-sm">
                  Remove
                </button>
              </div>
              {editingFloorId === floor.id && (
                <FloorEditRow
                  floor={floor}
                  mapId={mapId}
                  token={token}
                  onDone={() => setEditingFloorId(null)}
                  onChanged={onChanged}
                />
              )}
            </li>
          ))}
        </ul>
        <div className="border-t border-gray-700 pt-4 space-y-2">
          <input
            className="w-full p-2 text-black rounded"
            placeholder="Floor name (e.g. Ground Floor)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FloorImageSource
            token={token}
            file={file}
            setFile={setFile}
            urlValue={urlValue}
            setUrlValue={setUrlValue}
          />
          <textarea
            className="w-full p-2 text-black rounded"
            rows={2}
            placeholder='Heads-up note (optional -- e.g. "This section is randomly generated, positions may not be exact")'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <button onClick={handleAdd} disabled={uploading} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded w-full">
            {uploading ? "Uploading..." : "Add Floor"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LootMap() {
  const { gameName } = useParams();
  const [token, setToken] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [maps, setMaps] = useState([]);
  const [selectedMapId, setSelectedMapId] = useState(null);
  const [floors, setFloors] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [legend, setLegend] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [viewingMarker, setViewingMarker] = useState(null);
  const [editingMarker, setEditingMarker] = useState(null);
  const [showLegendEditor, setShowLegendEditor] = useState(false);
  const [showFloorManager, setShowFloorManager] = useState(false);
  const [showMapManager, setShowMapManager] = useState(false);
  const [floorImageStatus, setFloorImageStatus] = useState("loading");
  const [loadError, setLoadError] = useState(false);
  const imgRef = useRef(null);

  const canEdit =
    token &&
    ["owner", "management", "helper", "cryo_mapper"].includes(token.account.role);
  const canManageMaps =
    token && ["owner", "management", "helper"].includes(token.account.role);

  useEffect(() => {
    getTokenInternal().then(setToken);
  }, []);

  useEffect(() => {
    fetch(`${API}/api/games`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load games");
        return res.json();
      })
      .then((games) => {
        const match = games.find((g) => g.name === gameName);
        if (match) setGameId(match.id);
      })
      .catch(() => setLoadError(true));
  }, [gameName]);

  const authHeaders = token ? { Authorization: `Bearer ${token.access_token}` } : {};

  const loadMaps = useCallback(() => {
    if (!gameId) return;
    fetch(`${API}/api/loot/games/${gameId}/maps`, { headers: authHeaders })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load maps");
        return res.json();
      })
      .then((data) => {
        setLoadError(false);
        setMaps(data);
        if (data.length > 0) {
          setSelectedMapId((prev) => prev ?? data[0].id);
        }
      })
      .catch(() => setLoadError(true));
  }, [gameId, token]);

  const loadLegend = useCallback(() => {
    if (!gameId) return;
    fetch(`${API}/api/loot/games/${gameId}/legend`, { headers: authHeaders })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load legend");
        return res.json();
      })
      .then((data) => {
        setLoadError(false);
        setLegend(data);
      })
      .catch(() => setLoadError(true));
  }, [gameId, token]);

  useEffect(() => {
    loadMaps();
    loadLegend();
  }, [loadMaps, loadLegend]);

  const loadFloors = useCallback(() => {
    if (!selectedMapId) return;
    fetch(`${API}/api/loot/maps/${selectedMapId}/floors`, { headers: authHeaders })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load floors");
        return res.json();
      })
      .then((data) => {
        setLoadError(false);
        setFloors(data);
        setSelectedFloorId((prev) => {
          if (prev && data.some((f) => f.id === prev)) return prev;
          return data.length > 0 ? data[0].id : null;
        });
      })
      .catch(() => setLoadError(true));
  }, [selectedMapId, token]);

  useEffect(() => {
    loadFloors();
  }, [loadFloors]);

  const loadMarkers = useCallback(() => {
    if (!selectedFloorId) {
      setMarkers([]);
      return;
    }
    fetch(`${API}/api/loot/floors/${selectedFloorId}/markers`, { headers: authHeaders })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load markers");
        return res.json();
      })
      .then((data) => {
        setLoadError(false);
        setMarkers(data);
      })
      .catch(() => setLoadError(true));
  }, [selectedFloorId, token]);

  useEffect(() => {
    setFloorImageStatus("loading");
  }, [selectedFloorId]);

  useEffect(() => {
    loadMarkers();
  }, [loadMarkers]);

  const handleMarkerMove = async (marker, x_percent, y_percent) => {
    const payload = {
      floor_id: marker.floor_id,
      x_percent,
      y_percent,
      notes: marker.notes,
      photo_url: marker.photo_url,
      items: marker.items.map((i) => ({ legend_item_id: i.legend_item_id })),
    };
    await fetch(`${API}/api/loot/markers/${marker.id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    loadMarkers();
  };

  const handleImageClick = (e) => {
    if (!editMode || !imgRef.current || floorImageStatus !== "loaded") return;
    const rect = imgRef.current.getBoundingClientRect();
    const x_percent = ((e.clientX - rect.left) / rect.width) * 100;
    const y_percent = ((e.clientY - rect.top) / rect.height) * 100;
    setEditingMarker({
      floor_id: selectedFloorId,
      x_percent,
      y_percent,
      notes: "",
      photo_url: "",
      items: [],
    });
  };

  const currentFloor = floors.find((f) => f.id === selectedFloorId);
  const currentMap = maps.find((m) => m.id === selectedMapId);

  return (
    <div className="text-white min-h-screen">
      <Nav />
      <div className="max-w-6xl mx-auto p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold">{gameName} Loot Map</h1>

          {loadError && (
            <span className="text-sm text-red-400">
              Couldn't reach the server — try refreshing.
            </span>
          )}

          {canEdit && (
            <>
              <button
                onClick={() => setEditMode((v) => !v)}
                className={`px-3 py-2 rounded ${editMode ? "bg-yellow-600" : "bg-gray-700"}`}
              >
                {editMode ? "Exit Edit Mode" : "Edit Mode"}
              </button>
              <button onClick={() => setShowLegendEditor(true)} className="px-3 py-2 rounded bg-gray-700">
                Legend
              </button>
              {canManageMaps && (
                <button onClick={() => setShowMapManager(true)} className="px-3 py-2 rounded bg-gray-700">
                  Maps
                </button>
              )}
              <button onClick={() => setShowFloorManager(true)} className="px-3 py-2 rounded bg-gray-700">
                Floors
              </button>
            </>
          )}
        </div>

        {maps.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {maps.map((map) => (
              <button
                key={map.id}
                onClick={() => setSelectedMapId(map.id)}
                className={`px-3 py-2 rounded font-semibold ${
                  selectedMapId === map.id ? "bg-purple-600" : "bg-gray-700"
                }`}
              >
                {map.name}
                {map.private_testing && " 🔒"}
              </button>
            ))}
          </div>
        )}

        {currentMap && currentMap.description && (
          <div className="mb-3 p-3 bg-amber-900/40 border border-amber-600 rounded text-amber-200 text-sm">
            ⚠️ {currentMap.description}
          </div>
        )}

        {floors.length > 0 && (
          <div className="flex gap-2 mb-4 flex-wrap">
            {floors.map((floor) => (
              <button
                key={floor.id}
                onClick={() => setSelectedFloorId(floor.id)}
                className={`px-3 py-2 rounded ${
                  selectedFloorId === floor.id ? "bg-blue-600" : "bg-gray-700"
                }`}
              >
                {floor.name}
              </button>
            ))}
          </div>
        )}

        {currentFloor && currentFloor.description && (
          <div className="mb-3 p-3 bg-amber-900/40 border border-amber-600 rounded text-amber-200 text-sm">
            ⚠️ {currentFloor.description}
          </div>
        )}

        {legend.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4 text-sm">
            {legend.map((item) => (
              <button
                key={item.id}
                onClick={() =>
                  setHighlightedItemId((prev) => (prev === item.id ? null : item.id))
                }
                className={`flex items-center gap-1 px-2 py-1 rounded border ${
                  highlightedItemId === item.id
                    ? "border-yellow-300 bg-gray-800"
                    : "border-transparent hover:border-gray-600"
                }`}
              >
                <ItemSwatch item={item} sizeClass="w-5 h-5" />
                <span className="font-semibold">{item.code}</span>
                <span className="text-gray-400">= {item.name}</span>
              </button>
            ))}
          </div>
        )}

        {!currentFloor && !loadError && (
          <p className="text-gray-400">
            {maps.length === 0 ? (
              <>
                No map created yet.{" "}
                {canManageMaps && "Use the Maps button above to add one."}
              </>
            ) : (
              <>
                No floors yet.{" "}
                {canEdit && "Use the Floors button above to add one."}
              </>
            )}
          </p>
        )}

        {currentFloor && (
          <div className={`relative inline-block ${editMode && floorImageStatus === "loaded" ? "cursor-crosshair" : ""}`}>
            <img
              ref={imgRef}
              src={currentFloor.image_url}
              alt={currentFloor.name}
              onClick={handleImageClick}
              onLoad={() => setFloorImageStatus("loaded")}
              onError={() => setFloorImageStatus("error")}
              className="max-w-full rounded border border-gray-700"
            />
            {floorImageStatus === "error" && (
              <div className="mt-2 p-3 bg-red-900 border border-red-600 rounded text-red-200 text-sm">
                This floor's map image failed to load. Marker placement is disabled until
                it's fixed — check the image URL, or your connection, and try refreshing.
              </div>
            )}
            {floorImageStatus === "loaded" &&
              markers.map((marker) => (
                <MarkerDot
                  key={marker.id}
                  marker={marker}
                  highlightedItemId={highlightedItemId}
                  editMode={editMode}
                  containerRef={imgRef}
                  onMove={handleMarkerMove}
                  onClick={() => (editMode ? setEditingMarker(marker) : setViewingMarker(marker))}
                />
              ))}
          </div>
        )}
      </div>

      {viewingMarker && (
        <MarkerViewModal marker={viewingMarker} onClose={() => setViewingMarker(null)} />
      )}

      {editingMarker && (
        <MarkerEditModal
          marker={editingMarker}
          legend={legend}
          token={token}
          onClose={() => setEditingMarker(null)}
          onSaved={() => {
            setEditingMarker(null);
            loadMarkers();
          }}
          onDeleted={() => {
            setEditingMarker(null);
            loadMarkers();
          }}
        />
      )}

      {showLegendEditor && (
        <LegendEditorModal
          gameId={gameId}
          legend={legend}
          token={token}
          onClose={() => setShowLegendEditor(false)}
          onChanged={loadLegend}
        />
      )}

      {showFloorManager && (
        <FloorManagerModal
          mapId={selectedMapId}
          floors={floors}
          token={token}
          onClose={() => setShowFloorManager(false)}
          onChanged={loadFloors}
        />
      )}

      {showMapManager && (
        <MapManagerModal
          gameId={gameId}
          maps={maps}
          token={token}
          onClose={() => setShowMapManager(false)}
          onChanged={loadMaps}
        />
      )}
    </div>
  );
}
